import React from "react";
import { ApolloProvider, gql, useQuery, useMutation } from "@apollo/client";
import client from "../lib/apolloClient";
import SucceedoDashboard from "../components/Succeedo/Dashboard/SucceedoDashboard";
import { getIncompleteSections } from "../components/Succeedo/Dashboard/CompleteProfileBanner";

const SUCCEEDO_DASHBOARD_QUERY = gql`
  query SucceedoDashboard {
    currentUser {
      id
      name
      succeedo_persona
      succeedo_company_name
      province
      role_title
      industry_sector
      time_horizon
      owner_role
      business_size_band
      advisory_team_members {
        id
        role
        status
        name
        firm
      }
      action_plan {
        items(source: "succeedo") {
          id
          title
          description
          status
          priority
          partner_category
          cta_text
          metadata
          source
          documents {
            id
            name
            download_url
          }
        }
        open_count(source: "succeedo")
        completed_count(source: "succeedo")
      }
    }
    active_sprint {
      id
      name
      startOn
      endOn
      status
    }
    valuation_snapshots(limit: 1)
    financing_snapshots(limit: 1)
    deal_structure_snapshots(limit: 1)
    introduction_requests
    value_acceleration_scorecard
    featured_partners(limit: 3)
    my_advisory_team
    my_sponsorship
  }
`;

// Phase 5: add someone from the owner's advisor's network, or ask for an advisor.
const ADD_NETWORK_CONTACT = gql`
  mutation SponsorshipAddNetworkContact($contactId: ID!) {
    sponsorship_add_network_contact(contact_id: $contactId) { ok errors }
  }
`;
const REQUEST_ADVISOR = gql`mutation SponsorshipRequestAdvisor { sponsorship_request_advisor { ok errors } }`;

const ADD_ADVISORY_TEAM_MEMBER = gql`
  mutation AddAdvisoryTeamMember(
    $role: String!,
    $status: String!,
    $name: String,
    $email: String,
    $phone: String,
    $firm: String
  ) {
    add_advisory_team_member(
      role: $role,
      status: $status,
      name: $name,
      email: $email,
      phone: $phone,
      firm: $firm
    ) {
      advisory_team_member { id role status name email firm }
      errors
    }
  }
`;

const CREATE_INTRODUCTION_REQUEST = gql`
  mutation CreateIntroductionRequest($partnerCategory: String!, $actionPlanItemId: ID) {
    create_introduction_request(partner_category: $partnerCategory, action_plan_item_id: $actionPlanItemId) {
      introduction_request
      errors
    }
  }
`;

const UPDATE_ITEM = gql`
  mutation UpdateActionPlanItem($id: ID!, $status: ActionPlanItemStatusEnum) {
    update_action_plan_item(id: $id, status: $status) {
      id
      status
    }
  }
`;

function deriveInitials(name) {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ? parts[0][0] : "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

function computeDaysRemaining(endOn) {
  if (!endOn) return null;
  const end = new Date(endOn);
  const now = new Date();
  const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

export function SucceedoDashboardLoader({ mode = 'dashboard', topSlot = null } = {}) {
  const workspace = mode === 'workspace';
  const { data, loading, error, refetch } = useQuery(SUCCEEDO_DASHBOARD_QUERY);
  const [updateItem] = useMutation(UPDATE_ITEM);
  const [createIntroRequest, { loading: introLoading }] = useMutation(CREATE_INTRODUCTION_REQUEST);
  const [addAdvisoryTeamMember] = useMutation(ADD_ADVISORY_TEAM_MEMBER);
  const [addNetworkContact] = useMutation(ADD_NETWORK_CONTACT);
  const [requestAdvisor] = useMutation(REQUEST_ADVISOR);

  const handleStatusChange = async (itemId, newStatus) => {
    const statusUpper = String(newStatus).toUpperCase();
    try {
      await updateItem({
        variables: { id: itemId, status: statusUpper },
        optimisticResponse: {
          update_action_plan_item: {
            __typename: 'ActionPlanItem',
            id: itemId,
            status: newStatus,
          },
        },
      });
      refetch();
    } catch (e) {
      console.error('Failed to update item status', e);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500 text-sm">
          Something went wrong. Please try again.
        </div>
      </div>
    );
  }

  const user = data?.currentUser;

  // Users without a persona must complete the PersonaWizard first. In the
  // Workspace (George flag on), George captures the persona instead.
  if (user && !user.succeedo_persona && !workspace) {
    window.location.href = '/succeedo/onboarding';
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground text-sm">Redirecting...</div>
      </div>
    );
  }

  const persona = user?.succeedo_persona || "seller";
  const userName = user?.name || "";
  const userInitials = deriveInitials(userName);

  const actionPlan = user?.action_plan || {};
  const actionPlanItems = actionPlan.items || [];
  const openCount = actionPlan.open_count || 0;
  const completedCount = actionPlan.completed_count || 0;

  const sprint = data?.active_sprint;
  const daysRemaining = computeDaysRemaining(sprint?.endOn);
  const sprintExpired = sprint && daysRemaining === 0;

  const snapshots = data?.valuation_snapshots || [];
  const lastValuationSnapshot = snapshots.length > 0 ? snapshots[0] : null;

  const finSnapshots = data?.financing_snapshots || [];
  const lastFinancingSnapshot = finSnapshots.length > 0 ? finSnapshots[0] : null;

  const dealSnapshots = data?.deal_structure_snapshots || [];
  const lastDealStructureSnapshot = dealSnapshots.length > 0 ? dealSnapshots[0] : null;

  const introductionRequests = data?.introduction_requests || [];
  const scorecard = data?.value_acceleration_scorecard || null;
  const featuredPartners = data?.featured_partners || [];
  const profileIncompleteSections = getIncompleteSections(user);
  const advisoryTeam = data?.my_advisory_team || [];
  // Assessment "done" derives from the value-acceleration scorecard's
  // assessment object — the same source SummaryStrip uses.
  const hasAssessment = !!scorecard?.assessment;
  // Note: advisory_team_members is queried (see SUCCEEDO_DASHBOARD_QUERY) to
  // pre-warm Apollo's normalized cache for ReadyToConnectWidget on downstream
  // pages. SucceedoDashboard doesn't consume the array directly.

  // Save an advisor from the dashboard's "Add mine" modal. Throws on
  // server-side error so the modal can surface the message inline; on
  // success refetches to repaint the team block with the new seat.
  const handleAddAdvisor = async ({ role, name, email, phone, firm, status }) => {
    const { data: result } = await addAdvisoryTeamMember({
      variables: { role, name, email, phone, firm, status: status || 'added' },
    });
    const errors = result?.add_advisory_team_member?.errors || [];
    if (errors.length > 0) throw new Error(errors.join(', '));
    refetch();
  };

  const handleAddNetwork = async (contactId) => {
    const { data: result } = await addNetworkContact({ variables: { contactId } });
    const errors = result?.sponsorship_add_network_contact?.errors || [];
    if (errors.length > 0) throw new Error(errors.join(', '));
    refetch();
  };

  const handleRequestAdvisor = async () => {
    const { data: result } = await requestAdvisor();
    const errors = result?.sponsorship_request_advisor?.errors || [];
    if (errors.length > 0) throw new Error(errors.join(', '));
    refetch();
  };

  const handleFindPartner = (role) => {
    if (typeof window === 'undefined') return;
    window.location.href = `/succeedo/partners?role=${encodeURIComponent(role)}`;
  };

  const handleRequestIntroduction = async (partnerCategory, actionPlanItemId) => {
    try {
      const { data: result } = await createIntroRequest({
        variables: { partnerCategory, actionPlanItemId },
      });
      const errors = result?.create_introduction_request?.errors || [];
      if (errors.length > 0) {
        console.error('Introduction request errors:', errors);
        return { success: false, errors };
      }
      if (typeof window.ahoy !== 'undefined') {
        window.ahoy.track('succeedo_introduction_requested', {
          partner_category: partnerCategory,
          action_plan_item_id: actionPlanItemId,
        });
      }
      refetch();
      return { success: true };
    } catch (e) {
      console.error('Failed to create introduction request', e);
      return { success: false, errors: ['Something went wrong. Please try again.'] };
    }
  };

  return (
    <SucceedoDashboard
      persona={persona}
      userName={userName}
      userInitials={userInitials}
      actionPlanItems={actionPlanItems}
      openCount={openCount}
      completedCount={completedCount}
      daysRemaining={daysRemaining}
      sprintExpired={sprintExpired}
      onStatusChange={handleStatusChange}
      onRequestIntroduction={handleRequestIntroduction}
      introductionRequests={introductionRequests}
      introLoading={introLoading}
      lastValuationSnapshot={lastValuationSnapshot}
      lastFinancingSnapshot={lastFinancingSnapshot}
      lastDealStructureSnapshot={lastDealStructureSnapshot}
      scorecard={scorecard}
      featuredPartners={featuredPartners}
      profileIncompleteSections={profileIncompleteSections}
      advisoryTeam={advisoryTeam}
      hasAssessment={hasAssessment}
      onAddAdvisor={handleAddAdvisor}
      onFindPartner={handleFindPartner}
      sponsorship={data?.my_sponsorship || null}
      onAddNetwork={handleAddNetwork}
      onRequestAdvisor={handleRequestAdvisor}
      breadcrumb={workspace ? 'WORKSPACE' : 'DASHBOARD'}
      workspace={workspace}
      topSlot={topSlot}
    />
  );
}

const SucceedoDashboardContainer = (props, railsContext) => (
  <ApolloProvider client={client}>
    <SucceedoDashboardLoader />
  </ApolloProvider>
);

export default SucceedoDashboardContainer;
